import type { ObpSection } from "@credit-acceptance/step6-bff-client";
import { useCallback, useEffect, useRef, useState } from "react";
import { type FieldPath, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { SubsectionHeader } from "../../../../components/ui/subsectionHeader";
import { getErrorMessage } from "../../../../lib/query/errors";
import Section from "../../components/Section";
import { useRegisterSaveableSection } from "../../useRegisterSaveableSection";
import { ProductDisplayNameMap } from "./forms/constants";
import { StandardProductFields } from "./forms/StandardProductFields";
import { type ObpFormValues, toObpFormValues, toObpSaveRequest, translateObpViolations } from "./Obp.mapping";

interface ObpProperties {
  readonly obp?: ObpSection;
  readonly editable?: boolean;
  /** Locks inputs through the save and dashboard refresh without excluding the save payload. */
  readonly isSaving?: boolean;
}

function Obp({ obp, editable = false, isSaving = false }: ObpProperties) {
  const { t } = useTranslation();
  const form = useForm<ObpFormValues>({ defaultValues: toObpFormValues(obp) });
  const { reset, getValues, setError, clearErrors } = form;
  const appliedPaths = useRef<Array<FieldPath<ObpFormValues>>>([]);
  const [generalErrors, setGeneralErrors] = useState<string[]>([]);
  const [saveError, setSaveError] = useState<string>();
  const applyValidations = useCallback(
    (validations: ObpSection["validations"]) => {
      clearErrors(appliedPaths.current);
      const { errors, general } = translateObpViolations(validations, getValues().products);
      for (const [path, message] of errors) {
        setError(path, { type: "server", message });
      }
      appliedPaths.current = [...errors.keys()];
      setGeneralErrors(general);
    },
    [clearErrors, getValues, setError]
  );

  const snapshotKey = JSON.stringify(obp);
  useEffect(() => {
    reset(toObpFormValues(obp));
    applyValidations(obp?.validations);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- identical refetches must not discard unsaved edits
  }, [snapshotKey, reset, applyValidations]);

  useRegisterSaveableSection("obp", {
    // Like Driver, validations are authoritative BFF feedback, never a save gate.
    validate: async () => true,
    getPayload: () =>
      editable && obp?.products?.length ? { additionalProducts: { obp: toObpSaveRequest(getValues()) } } : {},
    onSaved: (outcome) => {
      setSaveError(undefined);
      reset(undefined, { keepValues: true, keepErrors: true });
      if (outcome.additionalProducts?.obp) {
        applyValidations(outcome.additionalProducts.obp.validations);
      }
    },
    onSaveFailed: (error) => setSaveError(getErrorMessage(error)),
  });

  return (
    <Section title={t("obp.sectionTitle")}>
      {generalErrors.map((message, index) => (
        <p key={`${index}-${message}`} role="alert" className="text-destructive text-2xs">
          {message}
        </p>
      ))}
      {saveError && (
        <p role="alert" className="text-destructive text-2xs">
          {saveError}
        </p>
      )}
      <div className="@container">
        <div className="@xl:grid-cols-2 @4xl:grid-cols-3 grid grid-cols-1 gap-x-6 gap-y-4">
          {(obp?.products ?? []).map((product, index) => (
            <div key={product.name} className="flex min-w-0 flex-col gap-2">
              <SubsectionHeader>{ProductDisplayNameMap[product.name] ?? product.name}</SubsectionHeader>
              <StandardProductFields
                form={form}
                index={index}
                productName={product.name}
                readOnly={!editable || isSaving}
              />
            </div>
          ))}
        </div>
      </div>
    </Section>
  );
}

export default Obp;
